import { events } from "@/common/constants";
import CustomSplitText from "@/mono/components/CustomSplitText";
import emitter from "@/mono/lib/emitter";
import useAnimation from "@/mono/lib/hooks/useAnimation";
import { useIsClient } from "@/mono/lib/hooks/useIsClient";
import useIsomorphicLayoutEffect from "@/mono/lib/hooks/useIsomorphicLayoutEffect";
import { fullHeight, setTypography } from "@/mono/lib/styles/mixins";
import { useGlobalStore } from "@/mono/store/global";

import { assetList } from "@/common/assets";
import { useEffect, useRef } from "react";
import { styled } from "styled-components";

import { TRANSITION_DURATION } from "../gl/GLBackground";
import { Overlay, overlayDuration } from "../../app/ClientLayout";
import { EASE_CUSTOM_3 } from "mono/lib/styles/easing";
import { gsap } from "mono/lib/gsap";
import { useProgress } from "@react-three/drei";
import {
  onProgress as subscribeToProgress,
  onComplete as subscribeToComplete,
  updateSourceProgress,
  markSourceComplete,
  waitForBrowserLoad,
  waitForFonts,
  startSourceTimeout,
} from "@/lib/loadingManager";
import {
  waitForThreeIdle,
  waitForImages,
  waitForVideos,
} from "@/lib/pageReady";

export const LOADER_OUT_DURATION = 1;

const format = (p) => `${p.toFixed(0)}`;

function LoaderView() {
  const hasLoaded = useRef(false);
  const progressText = useRef();
  const wrapper = useRef();
  const overlay = useRef();
  const isClient = useIsClient();

  const lenis = useGlobalStore((s) => s.lenis);

  // Track R3F progress for the "three" source
  const { progress: r3fProgress } = useProgress();

  const { animateOut } = useAnimation({
    inParams: {},
    outParams: {
      duration: TRANSITION_DURATION / 1000,
      delay: 0.5 + overlayDuration / 1000,
      ease: EASE_CUSTOM_3,
      onUpdate: (v) => {
        if (wrapper.current)
          wrapper.current.style.clipPath = `inset(${(1 - v) * 100}% 0 0 0)`;
      },
      onComplete: () => {
        emitter.emit(events.LOADING_OUT_COMPLETE);
      },
    },
  });

  function writeDisplayProgress(v) {
    if (progressText.current?.element) {
      progressText.current.element.innerText = format(v);
    }
  }

  // Initialize progress display
  useIsomorphicLayoutEffect(() => {
    writeDisplayProgress(0);
  }, []);

  // Start loading process
  useEffect(() => {
    const loaderBackground = document.querySelector(".loader-background");
    if (loaderBackground) loaderBackground.remove();

    const noWebGLImages = useGlobalStore.getState().noWebGLImages;

    if (noWebGLImages) {
      markSourceComplete("worker");
      markSourceComplete("three");
      markSourceComplete("dom");
      waitForFonts();
      waitForBrowserLoad();
      return;
    }

    emitter.emit(events.LOADING_START, assetList);

    const threeTimeout = startSourceTimeout("three");
    const domTimeout = startSourceTimeout("dom");

    Promise.all([
      waitForFonts(),
      waitForBrowserLoad(),
      waitForThreeIdle().then(() => {
        markSourceComplete("three");
      }),
      Promise.all([waitForImages(document), waitForVideos(document)]).then(
        () => {
          markSourceComplete("dom");
        }
      ),
    ]);

    return () => {
      clearTimeout(threeTimeout);
      clearTimeout(domTimeout);
    };
  }, []);

  // Fade in the progress text
  useEffect(() => {
    if (progressText.current?.element) {
      gsap.set(progressText.current.element, { opacity: 0 });
      gsap.to(progressText.current.element, {
        opacity: 1,
        duration: 0.4,
        ease: EASE_CUSTOM_3,
      });
    }
  }, []);

  // Subscribe to unified progress updates
  useEffect(() => {
    const unsubscribe = subscribeToProgress((progress) => {
      writeDisplayProgress(progress);
    });

    return unsubscribe;
  }, []);

  // Subscribe to completion event
  useEffect(() => {
    const unsubscribe = subscribeToComplete(() => {
      doAnimateOut();
    });

    return unsubscribe;
  }, []);

  // Sync R3F progress to the unified loading manager
  useEffect(() => {
    if (Number.isFinite(r3fProgress)) {
      updateSourceProgress("three", r3fProgress);
    }
  }, [r3fProgress]);

  // Listen for worker progress events
  useEffect(() => {
    function handleWorkerProgress({ progress }) {
      updateSourceProgress("worker", progress);
    }

    function handleWorkerComplete() {
      markSourceComplete("worker");
    }

    emitter.on(events.LOADING_PROGRESS, handleWorkerProgress);
    emitter.on(events.LOADING_COMPLETE, handleWorkerComplete);

    return () => {
      emitter.off(events.LOADING_PROGRESS, handleWorkerProgress);
      emitter.off(events.LOADING_COMPLETE, handleWorkerComplete);
    };
  }, []);

  function doAnimateOut() {
    if (hasLoaded.current) return;
    hasLoaded.current = true;

    if (wrapper.current) {
      if (progressText.current?.element) {
        gsap.to(progressText.current.element, {
          opacity: 0,
          duration: 0.3,
          ease: EASE_CUSTOM_3,
        });
      }
      animateOut();
      overlay.current?.classList.add("active");
      useGlobalStore.getState().lenis?.start();
      emitter.emit(events.LOADING_DONE);
    }
  }

  // Ensure we start at scroll 0 and prevent scrolling until loader hides
  useEffect(() => {
    if (!hasLoaded.current) {
      lenis?.stop?.();
      if (typeof window !== "undefined") window.scrollTo(0, 0);
    }
  }, [lenis]);

  if (!isClient) return null;

  return (
    <Wrapper ref={wrapper}>
      <Overlay ref={overlay} />
      <CustomSplitText
        immediate
        ref={progressText}
        className="loader-progress-text"
        animateOnScroll={false}
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  width: 100vw;
  ${fullHeight}
  z-index: 9999;
  top: 0;
  background-color: ${({ theme }) => theme.theme.menuBackground};
  position: absolute;
  overflow: hidden;
  max-height: 100vh;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  clip-path: inset(0 0 0 0);

  .loader-progress-text {
    ${({ theme: { locale } }) => setTypography("loaderText", locale)};
    color: ${({ theme }) => theme.theme.accent};
    position: absolute;
    margin-right: ${({ theme }) => theme.misc.basePadding()};
    margin-bottom: ${({ theme }) => theme.misc.basePadding()};
    text-align: right;
  }
`;

export default LoaderView;
